import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './SupportAutomation.css';
import './BenefitsSection.css';
import './floating.css';
import './SupportCards.css';

const SupportAutomation = () => {
  // Scroll to top when component mounts and initialize animations
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    // Initialize AOS animations
    AOS.init({
      duration: 800,
      easing: 'ease-out',
      once: false,
    });

    // Refresh AOS when all content is loaded
    window.addEventListener('load', () => {
      AOS.refresh();
    });

    return () => {
      // Clean up event listener
      window.removeEventListener('load', AOS.refresh);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Best AI-integrated 24/7 customer and lead genartaion support with Troubleshooting
          Assistance
        </title>
        <meta
          name="description"
          content="Best AI integrated Consistent and Scalable 24/7 customer automation support resulting in Enhanced Agent Productivity and cost reduction"
        />
        <meta
          name="keywords"
          content="Customer Support Automation, customer support 24/7, customer support automation software, ai customer service, ai customer service software, ai based customer support"
        />
        <link rel="canonical" href="https://www.aichatassist.com/features/support-automation" />

        {/* Open Graph Meta */}
        <meta
          property="og:title"
          content="AI Chat Assist for 24/7 Support | SG, UK & UAE Solutions"
        />
        <meta
          property="og:description"
          content="Automate customer support with AI Chat Assist. Engage users 24/7 across Singapore, UK & UAE with powerful AI tools for seamless customer experiences."
        />
        <meta
          property="og:url"
          content="https://www.aichatassist.com/features/support-automation"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Meta */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Chat Assist for 24/7 Support | SG, UK & UAE Solutions"
        />
        <meta
          name="twitter:description"
          content="Automate customer support with AI Chat Assist. Engage users 24/7 across Singapore, UK & UAE with powerful AI tools for seamless customer experiences."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      {/* Hero Section */}
      <section className="">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 text-lg-start">
              <div
                className="d-inline-flex align-items-center bg-dark text-white px-3 py-2 rounded-pill mb-4 hero-badge"
                style={{ background: `var(--purple-gradient)` }}
              >
                <span className="small">AI-Driven Customer Support Automation, 24/7</span>
              </div>

              <h1 className="page-hero-title" style={{ marginTop: '0px' }}>
                <span className="hero-bold" style={{ marginRight: '10px' }}>
                  Customer
                </span>
                <span className="hero-hero-highlight" style={{ marginRight: '10px' }}>
                  Support
                </span>
                <br />
                <span className="hero-bold">Automation</span>
              </h1>

              <p className="page-hero-text">
                Streamline your operations with customer support automation software that delivers{' '}
                <strong>customer support 24/7</strong>. Quickly resolve issues and guide users with
                our smart <strong>AI customer service</strong> tools.
              </p>
            </div>

            <div className="col-lg-4">
              <div className="page-hero-image">
                <img
                  src="/images/diamond_transparent.png"
                  alt="Support Automation"
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Background decorative elements */}
        {/* <div className="hero-background-circle-1"></div> */}
        {/* <div className="hero-background-circle-2"></div> */}
      </section>

      {/* Key Benefits Section */}
      <section id="benefits" className="benefits-section py-5">
        <div className="container">
          <div className="section-heading text-center">
            <h2>
              Key <span className="hero-highlight">Benefits</span>
            </h2>
            <p>
              Discover how our AI-based customer support system automates service, scales
              operations, and ensures 24/7 reliability with intelligent assistance.
            </p>
          </div>

          <div className="benefits-diagram">
            <div className="benefits-circle">
              <div className="center-circle">
                <h3>
                  Benefits of
                  <br />
                  <span>Support Automation</span>
                </h3>
              </div>

              <div className="benefit-item item-1 float-up delay-0">
                <div className="benefit-number">01</div>
                <div className="benefit-content">
                  <h4>24/7 Availability</h4>
                  <p>
                    Provide <strong>customer support 24/7</strong> with AI chatbots that respond
                    instantly—anytime.
                  </p>
                </div>
              </div>

              <div className="benefit-item item-2 float-down delay-1">
                <div className="benefit-number">02</div>
                <div className="benefit-content">
                  <h4>Cost Reduction</h4>
                  <p>
                    Cut expenses with <strong>customer support automation</strong> that handles
                    repetitive queries efficiently.
                  </p>
                </div>
              </div>

              <div className="benefit-item item-3 float-up delay-2">
                <div className="benefit-number">03</div>
                <div className="benefit-content">
                  <h4>Consistent Responses</h4>
                  <p>
                    Deliver brand-aligned replies with <strong>AI-based customer support</strong>{' '}
                    that never varies.
                  </p>
                </div>
              </div>

              <div className="benefit-item item-4 float-down delay-3">
                <div className="benefit-number">04</div>
                <div className="benefit-content">
                  <h4>Scalable Support</h4>
                  <p>
                    Adapt to high volumes effortlessly with{' '}
                    <strong>AI customer service software</strong>.
                  </p>
                </div>
              </div>

              <div className="benefit-item item-5 float-up delay-0">
                <div className="benefit-number">05</div>
                <div className="benefit-content">
                  <h4>Enhanced Agent Productivity</h4>
                  <p>Let AI handle routine tasks so agents can solve complex problems.</p>
                </div>
              </div>

              <div className="benefit-item item-6 float-down delay-1">
                <div className="benefit-number">06</div>
                <div className="benefit-content">
                  <h4>Valuable Insights</h4>
                  <p>Use AI to learn from support chats and refine your service.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Capabilities Section - Redesigned Cards */}
      <section className="capabilities-section py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2>
              Support <span className="hero-highlight">Capabilities</span>
            </h2>
            <p>Comprehensive support automation for various customer needs</p>
          </div>

          <div className="row g-4">
            {/* FAQ Automation Card */}
            <div className="col-md-6">
              <div className="support-card-wrapper fade-up-card delay-100">
                <div className="support-card">
                  <div className="support-card-header">
                    <div className="support-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.09 9.00002C9.3251 8.33169 9.78915 7.76813 10.4 7.40915C11.0108 7.05018 11.7289 6.91896 12.4272 7.03873C13.1255 7.15851 13.7588 7.52154 14.2151 8.06355C14.6713 8.60555 14.9211 9.29154 14.92 10C14.92 12 11.92 13 11.92 13"
                          stroke="#ce75ff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 17H12.01"
                          stroke="#ce75ff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="support-card-title">
                      <h3>FAQ Automation</h3>
                    </div>
                  </div>
                  <div className="support-card-body">
                    <p className="support-card-description">
                      Use AI customer service software to answer FAQs instantly with consistent,
                      brand-aligned replies.
                    </p>
                    <ul className="support-card-features">
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Knowledge base integration</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Natural language understanding</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Contextual responses</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Continuous learning from new questions</span>
                      </li>
                    </ul>
                  </div>
                  <div className="support-card-footer"></div>
                </div>
              </div>
            </div>

            {/* Troubleshooting Assistance Card */}
            <div className="col-md-6">
              <div className="support-card-wrapper fade-up-card delay-200">
                <div className="support-card">
                  <div className="support-card-header">
                    <div className="support-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M10.5 3.75H13.5C18.5 3.75 20.25 5.5 20.25 10.5V13.5C20.25 18.5 18.5 20.25 13.5 20.25H10.5C5.5 20.25 3.75 18.5 3.75 13.5V10.5C3.75 5.5 5.5 3.75 10.5 3.75Z"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9 12H15"
                          stroke="#ce75ff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 15V9"
                          stroke="#ce75ff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="support-card-title">
                      <h3>Troubleshooting Assistance</h3>
                    </div>
                  </div>
                  <div className="support-card-body">
                    <p className="support-card-description">
                      Resolve issues faster with AI-based customer support that guides users step by
                      step—no agent needed.
                    </p>
                    <ul className="support-card-features">
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Interactive problem-solving flows</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Visual guidance with images and videos</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Adaptive troubleshooting based on responses</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Seamless escalation when needed</span>
                      </li>
                    </ul>
                  </div>
                  <div className="support-card-footer"></div>
                </div>
              </div>
            </div>

            {/* Order Management Card */}
            <div className="col-md-6">
              <div className="support-card-wrapper fade-up-card delay-300">
                <div className="support-card">
                  <div className="support-card-header">
                    <div className="support-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8 12.2H15"
                          stroke="#ce75ff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 16.2H12.38"
                          stroke="#ce75ff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 6H14C16 6 16 5 16 4C16 2 15 2 14 2H10C9 2 8 2 8 4C8 6 9 6 10 6Z"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 4.02C19.33 4.2 21 5.43 21 10V16C21 20 20 22 15 22H9C4 22 3 20 3 16V10C3 5.44 4.67 4.2 8 4.02"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="support-card-title">
                      <h3>Order Management</h3>
                    </div>
                  </div>
                  <div className="support-card-body">
                    <p className="support-card-description">
                      Automate order queries like tracking, returns, and updates with customer
                      support automation tools.
                    </p>
                    <ul className="support-card-features">
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Order status tracking</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Return and exchange processing</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Order modification handling</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Shipping and delivery updates</span>
                      </li>
                    </ul>
                  </div>
                  <div className="support-card-footer"></div>
                </div>
              </div>
            </div>

            {/* Appointment Scheduling Card */}
            <div className="col-md-6">
              <div className="support-card-wrapper fade-up-card delay-400">
                <div className="support-card">
                  <div className="support-card-header">
                    <div className="support-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8 2V5"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 2V5"
                          stroke="#ce75ff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3 9H21"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                          stroke="#8c6fff"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11.995 13.7H12.005"
                          stroke="#ce75ff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.294 13.7H8.304"
                          stroke="#ce75ff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.294 16.7H8.304"
                          stroke="#ce75ff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="support-card-title">
                      <h3>Appointment Scheduling</h3>
                    </div>
                  </div>
                  <div className="support-card-body">
                    <p className="support-card-description">
                      Let users book or manage appointments 24/7 using AI customer service
                      automation.
                    </p>
                    <ul className="support-card-features">
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Calendar integration</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Availability checking</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Reminder notifications</span>
                      </li>
                      <li className="support-card-feature">
                        <div className="support-card-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <span>Rescheduling assistance</span>
                      </li>
                    </ul>
                  </div>
                  <div className="support-card-footer"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2>
              How It <span className="hero-highlight">Works</span>
            </h2>
            <p>Intelligent support automation that delivers exceptional customer experiences</p>
          </div>

          <div className="service-cards-container">
            <div className="row">
              <div className="col-md-4" data-aos="fade-up">
                <div className="service-card">
                  <div className="service-icon">
                    <img
                      src="/images/icons/chat-icon.svg"
                      alt="Natural Language"
                      className="service-img"
                    />
                  </div>
                  <h3>Natural Language Understanding</h3>
                  <p>
                    Our AI customer service software understands user intent, enabling accurate,
                    human-like responses in real time.
                  </p>
                  <ul className="feature-list-minimal">
                    <li>Advanced natural language processing</li>
                    <li>Intent recognition and entity extraction</li>
                    <li>Contextual understanding</li>
                    <li>Multi-language support</li>
                  </ul>
                </div>
              </div>

              <div className="col-md-4" data-aos="fade-up" data-aos-delay="100">
                <div className="service-card">
                  <div className="service-icon">
                    <img
                      src="/images/icons/kb-icon.svg"
                      alt="Knowledge Base"
                      className="service-img"
                    />
                  </div>
                  <h3>Knowledge Base Integration</h3>
                  <p>
                    Integrate existing documentation with our customer support automation to deliver
                    consistent answers across every channel.
                  </p>
                  <ul className="feature-list-minimal">
                    <li>Seamless integration with existing knowledge bases</li>
                    <li>Automatic content updates</li>
                    <li>Smart content retrieval</li>
                    <li>Gap analysis for content improvement</li>
                  </ul>
                </div>
              </div>

              <div className="col-md-4" data-aos="fade-up" data-aos-delay="200">
                <div className="service-card">
                  <div className="service-icon">
                    <img
                      src="/images/icons/escalation-icon.svg"
                      alt="Intelligent Escalation"
                      className="service-img"
                    />
                  </div>
                  <h3>Intelligent Escalation</h3>
                  <p>
                    AI detects when to escalate to agents, ensuring complex issues get expert
                    attention while routine queries stay automated.
                  </p>
                  <ul className="feature-list-minimal">
                    <li>Smart detection of complex issues</li>
                    <li>Sentiment analysis for frustrated customers</li>
                    <li>Complete conversation history transfer</li>
                    <li>Skill-based routing to appropriate agents</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      {/* <section className=" py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2>Helpdesk <span className="hero-highlight">Integrations</span></h2>
            <p>Seamlessly connect with your existing support systems</p>
          </div>

          <div className="row g-4 justify-content-center">
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/zendesk.png" alt="Zendesk" />
                <p>Zendesk</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/freshdesk.png" alt="Freshdesk" />
                <p>Freshdesk</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/intercom.png" alt="Intercom" />
                <p>Intercom</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/helpscout.png" alt="Help Scout" />
                <p>Help Scout</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/servicenow.png" alt="ServiceNow" />
                <p>ServiceNow</p>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="integration-logo">
                <img src="/images/integrations/salesforce-service.png" alt="Salesforce Service Cloud" />
                <p>Salesforce</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/platform/crm-api-integration" className="btn btn-outline-primary">View All Integrations</Link>
          </div>
        </div>
      </section> */}

      {/* Success Metrics Section */}
      {/* <section className=" py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2>Success <span className="hero-highlight">Metrics</span></h2>
            <p>Measurable improvements our customers experience</p>
          </div>

          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="metric-card text-center">
                <div className="metric-value">
                  <h3>80%</h3>
                </div>
                <div className="metric-label">
                  <p>Reduction in routine support tickets</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-3">
              <div className="metric-card text-center">
                <div className="metric-value">
                  <h3>60%</h3>
                </div>
                <div className="metric-label">
                  <p>Lower support costs</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-3">
              <div className="metric-card text-center">
                <div className="metric-value">
                  <h3>3x</h3>
                </div>
                <div className="metric-label">
                  <p>Faster response times</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-3">
              <div className="metric-card text-center">
                <div className="metric-value">
                  <h3>24/7</h3>
                </div>
                <div className="metric-label">
                  <p>Support availability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Success Stories Section
      <section className="success-stories-section py-5">
        <div className="container">
          <div className="section-heading text-center mb-5">
            <h2>Customer <span className="hero-highlight">Success Stories</span></h2>
            <p>Real results from businesses using our support automation</p>
          </div>

          <div className="row g-4">
            <div className="col-lg-4">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-logo">
                    <img src="/images/clients/client-4.png" alt="CloudTech Solutions" />
                  </div>
                  <h4>CloudTech Solutions</h4>
                  <p className="testimonial-industry">SaaS Provider</p>
                  <div className="testimonial-stats">
                    <div className="stat">
                      <h5>85%</h5>
                      <p>Support automation rate</p>
                    </div>
                    <div className="stat">
                      <h5>$320K</h5>
                      <p>Annual savings</p>
                    </div>
                  </div>
                  <p className="testimonial-quote">
                    "We implemented AI Chat Assist to handle our growing support volume without expanding our team. The chatbot now handles 85% of our support inquiries automatically, allowing our agents to focus on complex customer issues."
                  </p>
                  <p className="testimonial-author">- James Wilson, Customer Success Director</p>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-logo">
                    <img src="/images/clients/client-5.png" alt="GlobalShop" />
                  </div>
                  <h4>GlobalShop</h4>
                  <p className="testimonial-industry">E-commerce Retailer</p>
                  <div className="testimonial-stats">
                    <div className="stat">
                      <h5>92%</h5>
                      <p>Order status inquiries automated</p>
                    </div>
                    <div className="stat">
                      <h5>4.8/5</h5>
                      <p>Customer satisfaction</p>
                    </div>
                  </div>
                  <p className="testimonial-quote">
                    "Our support team was overwhelmed with basic order status inquiries. AI Chat Assist now handles these automatically, providing instant answers to customers while reducing our support queue by 65%."
                  </p>
                  <p className="testimonial-author">- Emma Thompson, Head of Customer Experience</p>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-logo">
                    <img src="/images/clients/client-6.png" alt="HealthPlus" />
                  </div>
                  <h4>HealthPlus</h4>
                  <p className="testimonial-industry">Healthcare Provider</p>
                  <div className="testimonial-stats">
                    <div className="stat">
                      <h5>24/7</h5>
                      <p>Patient support</p>
                    </div>
                    <div className="stat">
                      <h5>78%</h5>
                      <p>Appointment inquiries automated</p>
                    </div>
                  </div>
                  <p className="testimonial-quote">
                    "Our patients needed support outside business hours, but we couldn't staff a 24/7 call center. AI Chat Assist now provides instant answers about appointments, services, and common health questions at any time."
                  </p>
                  <p className="testimonial-author">- Dr. Robert Chen, Chief Digital Officer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}
    </>
  );
};

export default SupportAutomation;
